import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Box, Card, CardContent, CircularProgress, Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  EnumAppDocType,
  EnumAppSubmitted,
  EnumFileStatus,
  EnumSections,
  MAppEssay,
  MApplication,
  MAppSignature,
  MAppTranscript,
  MProfile
} from 'src/private/redux/scholarship.models';

import { ApplicationFormContainer } from './applications-form.styles';
import { getFileStatus } from '../../../shared/helpers/upload-helpers';
import HorizontalNonLinearStepperBar from '../../components/HorizontalNonLinearStepper/horizontal-non-linear-stepper-bar';
import HorizontalNonLinearStepperButtons from '../../components/HorizontalNonLinearStepper/horizontal-non-linear-stepper-buttons';
import {
  applicationSaveFile,
  fetchApplicationAsync,
  saveApplicationAsync,
  setApplicationId,
  setStep
} from '../../redux/scholarship.actions';
import {
  selectApplication,
  selectApplicationId,
  selectLinkedChildProfile,
  selectProfilesChild,
  selectStep,
  selectSteps,
  selectUnifiedError
} from '../../redux/scholarship.selectors';
import ApplicationsFormEssay from '../steps/ApplicationsFormEssay/applications-form-essay';
import ApplicationsFormFinancial from '../steps/ApplicationsFormFinancial/applications-form-financial';
import ApplicationsFormMeta from '../steps/ApplicationsFormMeta/applications-form-meta';
import ApplicationsFormSignatures from '../steps/ApplicationsFormSignatures/applications-form-signatures';
import ApplicationsFormTtpf from '../steps/ApplicationsFormTTPF/applications-form-ttpf';
import ApplicationsFormTranscript from '../steps/ApplicationsFormTranscript/applications-form-transcript';

export interface ComponentProps {
  id: string;
  userId?: string;
  url?: string;
  thisSection: unknown;
  scanning?: boolean;
  dispatchApplicationUpdate: (data: unknown) => void;
  dispatchResendEmail?: (data: unknown) => void;
  dispatchResendEmailClose?: (data: unknown) => void;
  hideSaveButtons?: (val: boolean) => void;
  dispatchResetFile?: (data: unknown) => void;
  dispatchDeleteFile?: (data: unknown) => void;
  dispatchSaveFile?: (data: unknown, section: string) => void;
  dispatchSaveMentionAsync?: (data: unknown) => void;
  dispatchSaveSignature?: (data: unknown) => void;
}

const useStyles = makeStyles((theme) => ({
  second: {
    marginTop: 15
  },
  short: {
    margin: '0 !important',
    padding: '0 !important'
  },
  mb: {
    marginBottom: 30
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)'
  },
  title: {
    fontSize: 14
  },
  pos: {
    marginBottom: 12
  },
  buttons: {
    width: '100%',
    textAlign: 'center'
  },
  button: {
    height: '45px',
    marginLeft: '15px',
    marginRight: '15px'
  },
  section: {
    margin: '36px'
  },
  [theme.breakpoints.down('xs')]: {
    section: {
      margin: 0
    }
  }
}));

interface IStepItem {
  id: string;
  label: string;
  num: number;
}

const ApplicationsForm = ({
  dispatchSetApplicationId,
  dispatchApplicationSave,
  dispatchSetStep,
  dispatchFetchApplicationAsync,
  dispatchSaveFile
}) => {
  const { applicationId: id, step: stepParam } = useParams();
  const navigate = useNavigate();
  const currentApp = useSelector<unknown, MApplication | undefined>(selectApplication);
  const currentApplicationId = useSelector<unknown, string | undefined>(selectApplicationId);
  const steps = useSelector<unknown, IStepItem[]>(selectSteps);
  const currentStep = useSelector<unknown, number>(selectStep);
  const profilesChild = useSelector<unknown, MProfile[]>(selectProfilesChild);
  const linkedChildProfile = useSelector<unknown, string | undefined>(selectLinkedChildProfile);
  const error = useSelector(selectUnifiedError);
  const appSubmitted = currentApp?.meta ? currentApp.meta.submitted === EnumAppSubmitted.YES : false;
  const [scanningEssay, setScanningEssay] = useState<boolean>(false);
  const [scanningTranscript, setScanningTranscript] = useState<boolean>(false);

  const classes = useStyles();

  const [forLine, setForLine] = useState<string | null>(null);
  const [hideSave, setHideSave] = useState<boolean>(false);
  const { trackPageView } = useMatomo();

  const errorRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  // Track page view
  useEffect(() => {
    trackPageView({});
  }, []);

  let intervalObj: Record<string, { interval: NodeJS.Timeout | number }> = {
    essay: {
      interval: 0
    },
    transcript: {
      interval: 0
    }
  };

  const handleFileStatus = async (fileType: EnumAppDocType) => {
    if (!id) {
      return;
    }

    const fileUpdate = await getFileStatus(id, EnumAppDocType[fileType.toUpperCase()]);
    if (
      fileUpdate &&
      (fileUpdate.docStatus === EnumFileStatus.RECEIVED ||
        fileUpdate.docStatus === EnumFileStatus.FAILED ||
        fileUpdate.docStatus === EnumFileStatus.MALICIOUS)
    ) {
      dispatchSaveFile(fileUpdate, fileType);
      if (fileType === EnumAppDocType.ESSAY) {
        setScanningEssay(false);
      }

      if (fileType === EnumAppDocType.TRANSCRIPT) {
        setScanningTranscript(false);
      }
      clearInterval(intervalObj[fileType].interval);
    }
  };

  const handleSetInterval = (triggerObj: { filePath?: string; docStatus?: string }, fileType: EnumAppDocType) => {
    if (triggerObj.filePath && (triggerObj.docStatus === EnumFileStatus.PENDING || triggerObj.docStatus === EnumFileStatus.DELETED)) {
      intervalObj[fileType].interval = setInterval(() => handleFileStatus(fileType), 5000);
      if (fileType === EnumAppDocType.ESSAY) {
        setScanningEssay(true);
      }

      if (fileType === EnumAppDocType.TRANSCRIPT) {
        setScanningTranscript(true);
      }

      return () => {
        clearInterval(intervalObj[fileType].interval);
      };
    }
  };

  useEffect(() => {
    if (currentApp?.essay) {
      handleSetInterval(currentApp.essay, EnumAppDocType.ESSAY);
    }
  }, [currentApp?.essay]);

  useEffect(() => {
    if (currentApp?.transcript) {
      handleSetInterval(currentApp.transcript, EnumAppDocType.TRANSCRIPT);
    }
  }, [currentApp?.transcript]);

  useEffect(() => {
    const evaluateMatch = () => {
      if (stepParam && stepParam !== steps[currentStep].id) {
        const newStep = steps.find((item) => item.id === stepParam);
        if (newStep) {
          dispatchSetStep(undefined, newStep.num);
        }
      }
    };
    evaluateMatch();
  }, [stepParam, steps, currentStep, dispatchSetStep]);

  useEffect(() => {
    const getChildName = () => {
      if (linkedChildProfile && profilesChild) {
        let thisProfile = profilesChild.find((item) => item.id === linkedChildProfile);
        setForLine(' for ' + thisProfile?.firstName);
      }
    };
    getChildName();
  }, [linkedChildProfile]);

  useEffect(() => {
    const setId = () => {
      if (id && id !== 'undefined') {
        if (currentApp?.meta && currentApp.meta.id !== id) {
          // We directly loaded a different ID than state, go back to the list so we can reload
          navigate('/applications');

          return;
        }

        if (currentApplicationId !== id) {
          dispatchSetApplicationId(id);
        }

        if (!currentApp?.meta) {
          dispatchFetchApplicationAsync(id).then();
        }
      } else {
        navigate('/applications');

        return;
      }
    };
    setId();
  }, [id, currentApplicationId, dispatchFetchApplicationAsync, dispatchSetApplicationId, navigate]);

  const hideSaveButtons = (val: boolean) => {
    setHideSave(val);
  };

  const focusErrorMessage = () => {
    errorRef.current?.scrollIntoView(true);
    errorRef.current?.focus();
  };

  const afterNextCallback = (stepLabel: string, newStep: number, newCompleted: object) => {
    if (!id) {
      navigate('/applications');

      return;
    }

    if (!currentApp) {
      return;
    }

    const savePromise = dispatchApplicationSave(id, stepLabel, currentApp[stepLabel], newCompleted);
    if (!savePromise) {
      return;
    }

    savePromise.then((result) => {
      if (result) {
        dispatchSetStep(currentStep, newStep);
        if (typeof result === 'string') {
          navigate(`/applications/${result}/${steps[newStep].id}`);
        } else {
          navigate(`/applications/${id}/${steps[newStep].id}`);
        }
      }
    });
  };

  useEffect(() => {
    if (error) {
      focusErrorMessage();
    }
  }, [error]);

  if (!id) {
    return (
      <ApplicationFormContainer>
        <Box m="auto">
          <CircularProgress />
        </Box>
        <Typography variant="h4">Loading application....</Typography>
      </ApplicationFormContainer>
    );
  }

  if (currentApp?.meta) {
    return (
      <ApplicationFormContainer>
        <Grid container>
          <Grid item className={classes.mb}>
            <Typography variant="h4">
              {id === 'new' ? 'New ' : ''}Scholarship Application
              {forLine}
            </Typography>
          </Grid>
        </Grid>
        <Card className={classes.short}>
          <CardContent className={classes.short}>
            <HorizontalNonLinearStepperBar id={id} />
          </CardContent>
        </Card>
        <Card className={classes.second}>
          <CardContent>
            {error && (
              <Alert severity="error" ref={errorRef}>
                {error}
              </Alert>
            )}
            {currentApp && (
              <div className={classes.section}>
                {
                  {
                    '0': <ApplicationsFormMeta id={id} thisSection={currentApp.meta} />,
                    '1': <ApplicationsFormTtpf id={id} thisSection={currentApp.ttpf} hideSaveButtons={hideSaveButtons} />,
                    '2': <ApplicationsFormFinancial id={id} thisSection={currentApp.financial} />,
                    '3': (
                      <ApplicationsFormEssay
                        id={id}
                        userId={currentApp.meta?.userId}
                        scanning={scanningEssay}
                        thisSection={currentApp.essay}
                      />
                    ),
                    '4': (
                      <ApplicationsFormTranscript
                        id={id}
                        userId={currentApp.meta?.userId || ''}
                        scanning={scanningTranscript}
                        thisSection={currentApp.transcript}
                      />
                    ),
                    '5': (
                      <ApplicationsFormSignatures
                        id={id}
                        focusErrors={focusErrorMessage}
                        scanningEssay={scanningEssay}
                        scanningTranscript={scanningTranscript}
                      />
                    )
                  }[currentStep]
                }
              </div>
            )}

            {!hideSave && currentStep !== 5 && (
              <Grid container justifyContent="space-between">
                <Grid item xs={12}>
                  <HorizontalNonLinearStepperButtons id={id} appSubmitted={appSubmitted} afterNext={afterNextCallback} />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </ApplicationFormContainer>
    );
  } else {
    return (
      <ApplicationFormContainer>
        <Box m="auto">
          <CircularProgress />
        </Box>
        <Typography variant="h4">Loading application....</Typography>
      </ApplicationFormContainer>
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDispatchToProps = (dispatch: any) => ({
  dispatchSetApplicationId: (item: string): void => {
    dispatch(setApplicationId(item));
  },
  dispatchApplicationSave: (id: string, section: EnumSections, values: unknown, progress: unknown): Promise<unknown> => {
    return dispatch(saveApplicationAsync(id, section, values, progress)) as Promise<unknown>;
  },
  dispatchSetStep: (oldStep: number | undefined, newStep: number): void => {
    dispatch(setStep(oldStep, newStep));
  },
  dispatchFetchApplicationAsync: (id: string): Promise<unknown> => {
    return dispatch(fetchApplicationAsync(id)) as Promise<unknown>;
  },
  dispatchSaveFile: (saveFile: MAppEssay | MAppTranscript | MAppSignature | Record<string, unknown>, section: EnumAppDocType): void => {
    dispatch(applicationSaveFile(saveFile, section));
  }
});

export default connect(null, mapDispatchToProps)(ApplicationsForm);
